import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    LineChart,
    BarChart,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
} from "recharts";
import { ArrowRight, TrendingUp, Trophy, Layers } from "lucide-react";

import { useDashboard } from "@/lib/dataContext";
import { PageHeader } from "@/components/common/PageHeader";
import { ChartCard } from "@/components/common/ChartCard";
import { Delta } from "@/components/common/Delta";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ChartTooltip, axisProps, gridProps } from "@/components/common/ChartTooltip";
import { fmt, fmtDecimal, fmtPct, chartColor, youthPct, GROUPS, groupColor, GENDER_COLORS } from "@/lib/format";

const INDEX_SERIES = [
    { key: "youth_representation_index", label: "Youth Representation" },
    { key: "wing_balance_index", label: "Wing Balance" },
    { key: "programme_variety_score", label: "Activity Diversity" },
    { key: "programme_concentration_index", label: "Activity Concentration" },
    { key: "attendance_concentration_index", label: "Attendance Concentration" },
];

export default function Trends() {
    const { data } = useDashboard();
    const months = data.monthly_trend.months;
    const wingTrend = data.monthly_trend.monthly_wing_trend;
    const groupComparisons = data.monthly_trend.monthly_group_comparisons;
    const defs = data.analytical_metric_definitions;
    const reportingPeriod = data?.reporting_period?.period_label || "the current reporting period";

    const [indexKey, setIndexKey] = useState("youth_representation_index");
    const [groupCode, setGroupCode] = useState("total");

    // annual highlights computed client-side
    const highlights = useMemo(() => {
        const byActivity = [...months].sort((a, b) => b.reported_activities - a.reported_activities)[0];
        const byAttendance = [...months].sort((a, b) => b.total_attendance - a.total_attendance)[0];
        const byYouth = [...months].sort((a, b) => youthPct(b) - youthPct(a))[0];
        return { byActivity, byAttendance, byYouth };
    }, [months]);

    const comboData = months.map((q) => ({
        month: q.month,
        Activities: q.reported_activities,
        "Avg Attendance": q.average_attendance_per_activity,
        "Youth %": youthPct(q),
    }));

    const genderData = months.map((q) => {
        const g = q.gender_engagement || {};
        return {
            month: q.month,
            "Gents (Senior)": g.gents_senior || 0,
            "Gents (Youth)": g.gents_youth || 0,
            "Ladies (Senior)": g.ladies_senior || 0,
            "Ladies (Youth)": g.ladies_youth || 0,
        };
    });

    const totalMoM = groupComparisons.total || [];

    // group trend from quarterly_wing_trend filtered by selected group
    const groupSeries = useMemo(
        () =>
            wingTrend
                .filter((w) => w.group_code === groupCode)
                .sort((a, b) => a.month_key.localeCompare(b.month_key))
                .map((w) => ({
                    month: w.month,
                    Activities: w.reported_activities,
                    Attendance: w.total_attendance,
                    Avg: w.average_attendance_per_activity,
                    Youth: w.youth_participation_pct,
                    Beneficiaries: w.beneficiaries,
                })),
        [wingTrend, groupCode],
    );
    const groupMoM = groupComparisons[groupCode] || [];
    const activeGroup = GROUPS.find((g) => g.code === groupCode);

    const activeIndex = INDEX_SERIES.find((s) => s.key === indexKey);
    const activeDef = defs[indexKey];
    const indexData = months.map((q) => ({
        month: q.month,
        value: q.analytical_indices[indexKey].value,
    }));

    return (
        <div className="space-y-8">
            <PageHeader
                eyebrow="Monthly Trends"
                title="How the year unfolded"
                description={`Activity, attendance and engagement movement across ${reportingPeriod}.`}
            />

            {/* Annual highlights */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {[
                    { label: "Peak Activity Month", value: highlights.byActivity.month, note: `${fmt(highlights.byActivity.reported_activities)} activities` },
                    { label: "Peak Attendance Month", value: highlights.byAttendance.month, note: `${fmt(highlights.byAttendance.total_attendance)} attendance` },
                    { label: "Top Youth Month", value: highlights.byYouth.month, note: `${fmtPct(youthPct(highlights.byYouth))} youth participation` },
                ].map((h, i) => (
                    <motion.div key={h.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                        <Card className="flex items-center gap-4 border-border/70 bg-card p-5 shadow-card">
                            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                                <Trophy className="h-6 w-6" />
                            </span>
                            <div>
                                <p className="font-display text-2xl font-semibold text-foreground">{h.value}</p>
                                <p className="text-xs font-medium text-foreground">{h.label}</p>
                                <p className="text-xs text-muted-foreground">{h.note}</p>
                            </div>
                        </Card>
                    </motion.div>
                ))}
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
                <ChartCard className="lg:col-span-3" title="Activities & Engagement" description="Bars: activities · Lines: avg attendance and youth %">
                    <div className="h-[320px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={comboData} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                                <CartesianGrid {...gridProps} />
                                <XAxis dataKey="month" {...axisProps} />
                                <YAxis yAxisId="left" {...axisProps} />
                                <YAxis yAxisId="right" orientation="right" {...axisProps} unit="%" />
                                <Tooltip content={<ChartTooltip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
                                <Bar yAxisId="left" dataKey="Activities" fill={chartColor(1)} radius={[6, 6, 0, 0]} maxBarSize={52} />
                                <Line yAxisId="left" type="monotone" dataKey="Avg Attendance" stroke={chartColor(3)} strokeWidth={2.5} dot={{ r: 3 }} />
                                <Line yAxisId="right" type="monotone" dataKey="Youth %" stroke={chartColor(2)} strokeWidth={2.5} strokeDasharray="5 4" dot={{ r: 3 }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </ChartCard>

                <ChartCard className="lg:col-span-2" title="Engagement by Gender & Age" description="Attendance split across gents/ladies and youth/senior">
                    <div className="h-[320px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={genderData} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                                <CartesianGrid {...gridProps} />
                                <XAxis dataKey="month" {...axisProps} />
                                <YAxis {...axisProps} />
                                <Tooltip content={<ChartTooltip formatter={(v) => fmt(v)} />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
                                <Legend iconType="circle" wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                                <Bar dataKey="Gents (Senior)" stackId="a" fill={GENDER_COLORS.gents_senior} maxBarSize={52} />
                                <Bar dataKey="Gents (Youth)" stackId="a" fill={GENDER_COLORS.gents_youth} maxBarSize={52} />
                                <Bar dataKey="Ladies (Senior)" stackId="a" fill={GENDER_COLORS.ladies_senior} maxBarSize={52} />
                                <Bar dataKey="Ladies (Youth)" stackId="a" fill={GENDER_COLORS.ladies_youth} radius={[6, 6, 0, 0]} maxBarSize={52} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </ChartCard>
            </div>

            {/* QoQ comparison (total) */}
            <div>
                <div className="mb-4 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    <h2 className="font-display text-xl font-semibold text-foreground">Month-on-Month Change</h2>
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    {totalMoM.map((c, i) => (
                        <motion.div key={c.comparison} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                            <Card className="h-full border-border/70 bg-card p-5 shadow-card">
                                <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                                    <span>{c.from_month}</span>
                                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                                    <span>{c.to_month}</span>
                                </div>
                                <div className="mt-4 space-y-3">
                                    {[
                                        { k: "Activities", v: c.activity_change_pct },
                                        { k: "Attendance", v: c.attendance_change_pct },
                                        { k: "Avg Attendance", v: c.average_attendance_change_pct },
                                        { k: "Beneficiaries", v: c.beneficiary_change_pct },
                                    ].map((row) => (
                                        <div key={row.k} className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground">{row.k}</span>
                                            <Delta value={row.v} />
                                        </div>
                                    ))}
                                    <div className="flex items-center justify-between border-t border-border/60 pt-3">
                                        <span className="text-sm text-muted-foreground">Youth participation</span>
                                        <Delta value={c.youth_participation_change_percentage_points} suffix=" pts" />
                                    </div>
                                </div>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Activity group trend */}
            <ChartCard
                title="Trend by Activity Group"
                description="Select a group to see its monthly trajectory and month-by-month change"
                action={
                    <span className="hidden items-center gap-1.5 text-xs text-muted-foreground sm:flex"><Layers className="h-4 w-4" /> Group view</span>
                }
            >
                <Tabs value={groupCode} onValueChange={setGroupCode} className="w-full">
                    <TabsList className="flex w-full flex-wrap justify-start">
                        {GROUPS.map((g) => (
                            <TabsTrigger key={g.code} value={g.code} className="text-xs">{g.name}</TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>

                <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-5">
                    <div className="lg:col-span-3">
                        <div className="h-[300px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={groupSeries} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                                    <CartesianGrid {...gridProps} />
                                    <XAxis dataKey="month" {...axisProps} />
                                    <YAxis yAxisId="left" {...axisProps} />
                                    <YAxis yAxisId="right" orientation="right" {...axisProps} />
                                    <Tooltip content={<ChartTooltip formatter={(v) => fmt(v)} />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
                                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                                    <Bar yAxisId="left" name="Activities" dataKey="Activities" fill={groupColor(groupCode)} radius={[6, 6, 0, 0]} maxBarSize={52} />
                                    <Line yAxisId="right" name="Attendance" type="monotone" dataKey="Attendance" stroke={chartColor(3)} strokeWidth={2.5} dot={{ r: 3 }} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                        {/* QoQ chips for group */}
                        <div className="mt-4 flex flex-wrap gap-2">
                            {groupMoM.map((c) => (
                                <div key={c.comparison} className="flex items-center gap-2 rounded-full border border-border/60 bg-secondary/40 px-3 py-1.5 text-xs">
                                    <span className="font-medium text-foreground">{c.comparison}</span>
                                    <span className="text-muted-foreground">Activities</span>
                                    <Delta value={c.activity_change_pct} />
                                    <span className="text-muted-foreground">Attend.</span>
                                    <Delta value={c.attendance_change_pct} />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-2">
                        <p className="mb-2 text-sm font-medium text-foreground">{activeGroup?.name} &mdash; absolute data points</p>
                        <div className="overflow-x-auto rounded-lg border border-border/60">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-border/70 hover:bg-transparent">
                                        <TableHead>Qtr</TableHead>
                                        <TableHead className="text-right">Acts</TableHead>
                                        <TableHead className="text-right">Attend.</TableHead>
                                        <TableHead className="text-right">Avg</TableHead>
                                        <TableHead className="text-right">Youth%</TableHead>
                                        <TableHead className="text-right">Benef.</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {groupSeries.map((r) => (
                                        <TableRow key={r.month} className="border-border/50">
                                            <TableCell className="font-medium text-foreground">{r.month}</TableCell>
                                            <TableCell className="text-right tabular">{fmt(r.Activities)}</TableCell>
                                            <TableCell className="text-right tabular">{fmt(r.Attendance)}</TableCell>
                                            <TableCell className="text-right tabular">{fmtDecimal(r.Avg)}</TableCell>
                                            <TableCell className="text-right tabular">{fmtPct(r.Youth)}</TableCell>
                                            <TableCell className="text-right tabular">{fmt(r.Beneficiaries)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                </div>
            </ChartCard>

            {/* Index trajectory + definition */}
            <ChartCard
                title="Health Index Trajectory"
                description={activeIndex.label + " across the year"}
            >
                <Tabs value={indexKey} onValueChange={setIndexKey} className="w-full">
                    <TabsList className="flex w-full flex-wrap justify-start">
                        {INDEX_SERIES.map((s) => (
                            <TabsTrigger key={s.key} value={s.key} className="text-xs">{s.label}</TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>

                <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-5">
                    <div className="lg:col-span-3">
                        <div className="h-[280px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={indexData} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
                                    <CartesianGrid {...gridProps} />
                                    <XAxis dataKey="month" {...axisProps} />
                                    <YAxis {...axisProps} domain={[0, "auto"]} />
                                    {indexKey === "youth_representation_index" && (
                                        <ReferenceLine y={1} stroke={chartColor(3)} strokeDasharray="4 4" label={{ value: "Ideal 1.0", fill: "hsl(var(--muted-foreground))", fontSize: 11, position: "insideTopRight" }} />
                                    )}
                                    <Tooltip content={<ChartTooltip formatter={(v) => fmtDecimal(v, 3)} />} />
                                    <Line type="monotone" dataKey="value" name={activeIndex.label} stroke={chartColor(1)} strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <div className="lg:col-span-2">
                        <Card className="h-full border-border/60 bg-secondary/30 p-4">
                            <p className="text-sm font-semibold text-foreground">{activeDef.dashboard_name}</p>
                            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{activeDef.short_description}</p>
                            <div className="mt-3 flex flex-wrap gap-2">
                                <span className="rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary">Ideal: {String(activeDef.ideal_value)}</span>
                                <span className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">Range: {activeDef.minimum_value}–{activeDef.maximum_value ?? "∞"}</span>
                            </div>
                            <p className="mt-3 font-mono text-[11px] text-muted-foreground">{activeDef.formula}</p>
                            <div className="mt-3 space-y-1">
                                {Object.entries(activeDef.interpretation).map(([band, meaning]) => (
                                    <div key={band} className="flex items-center justify-between text-xs">
                                        <span className="font-mono text-muted-foreground">{band}</span>
                                        <span className="text-foreground">{meaning}</span>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>
                </div>
            </ChartCard>
        </div>
    );
}
